import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import { getMyProfile } from '../api/users';
import { User } from '@tinyrewards/shared';

export default function MyProfilePage() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getMyProfile()
            .then(res => {
                if (res.success && res.data) {
                    setUser(res.data);
                } else {
                    setError(res.message ?? 'Failed to load profile.');
                }
            })
            .catch(() => setError('An unexpected error occurred.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !user) {
        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Alert severity="error">{error ?? 'Profile not found.'}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Avatar
                        src={user.profileImage}
                        sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'grey.400', fontSize: 40 }}
                    >
                        {user.displayName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="h5" fontWeight={700} gutterBottom>{user.displayName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" />
                        {user.email}
                    </Typography>
                </CardContent>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 2, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        component={Link}
                        to="/account"
                        startIcon={<EditIcon />}
                    >
                        Edit Profile
                    </Button>
                    {user.publicPageShare && (
                        <Button
                            component={Link}
                            to={`/u/${user.userID}`}
                            startIcon={<PublicIcon />}
                        >
                            View public profile
                        </Button>
                    )}
                </Box>
            </Card>
        </Container>
    );
}
